import { z } from 'zod';
export const communicationsPortalProjectMessagesCapability = 'communications.portal-project-messages.v1';
export const communicationsPortalProjectMessageScopeProviderId = 'communications.server.portal-project-message-scope';
export const communicationsPortalProjectThreadResourceType = 'communications.portal-project-thread';
export const communicationsPortalProjectMessagesReadGrant = 'communications.portal-messages.read';
export const communicationsPortalProjectMessagesWriteGrant = 'communications.portal-messages.write';
export const communicationsPortalProjectMessageSenderTypeSchema = z.enum([
    'customer',
    'staff',
]);
export const communicationsPortalProjectMessageSchema = z.strictObject({
    id: z.string().uuid(),
    senderType: communicationsPortalProjectMessageSenderTypeSchema,
    senderName: z.string().trim().min(1).max(100).nullable(),
    body: z.string().trim().min(1).max(2_000),
    createdAt: z.string().datetime({ offset: true }),
});
export const communicationsPortalProjectMessagesResponseSchema = z.strictObject({
    contractVersion: z.literal(communicationsPortalProjectMessagesCapability),
    data: z.array(communicationsPortalProjectMessageSchema).max(100),
    summary: z.strictObject({
        messageCount: z.number().int().min(0).max(100),
        needsReply: z.boolean(),
        lastMessageAt: z.string().datetime({ offset: true }).nullable(),
    }),
}).superRefine((response, context) => {
    if (response.summary.messageCount !== response.data.length) {
        context.addIssue({
            code: 'custom', path: ['summary', 'messageCount'],
            message: 'Portal message count must reconcile to the returned history.',
        });
    }
    const last = response.data.at(-1) ?? null;
    if (response.summary.lastMessageAt !== (last?.createdAt ?? null)) {
        context.addIssue({
            code: 'custom', path: ['summary', 'lastMessageAt'],
            message: 'Portal last-message time must match the returned history.',
        });
    }
    if (response.summary.needsReply !== (last?.senderType === 'customer')) {
        context.addIssue({
            code: 'custom', path: ['summary', 'needsReply'],
            message: 'Portal needs-reply state must follow the latest message direction.',
        });
    }
    for (let index = 1; index < response.data.length; index += 1) {
        const previous = response.data[index - 1];
        const current = response.data[index];
        if (previous !== undefined && current !== undefined &&
            (previous.createdAt > current.createdAt ||
                (previous.createdAt === current.createdAt && previous.id > current.id))) {
            context.addIssue({
                code: 'custom', path: ['data', index],
                message: 'Portal messages must be chronological.',
            });
        }
    }
});
export const communicationsPortalProjectMessagePostSchema = z.strictObject({
    commandId: z.string().uuid(),
    senderName: z.string().trim().min(1).max(100),
    body: z.string().trim().min(1).max(2_000),
});
export const communicationsStaffPortalProjectMessagePostSchema = z.strictObject({
    commandId: z.string().uuid(),
    projectId: z.string().uuid(),
    body: z.string().trim().min(1).max(2_000),
});
export const communicationsPortalProjectMessageCommandResponseSchema = z.strictObject({
    contractVersion: z.literal(communicationsPortalProjectMessagesCapability),
    data: communicationsPortalProjectMessageSchema,
    idempotent: z.boolean(),
});
export const communicationsPortalProjectNotificationAudienceStrategy = 'assigned-project-team-then-owner';
export const communicationsPortalProjectNotificationRequestedEventName = 'communications.portal-project-notification-requested.v1';
export const communicationsPortalProjectNotificationRequestedEventVersion = 1;
export const communicationsPortalProjectMessageSystemNotificationSourceModule = 'communications';
export const communicationsPortalProjectMessageSystemNotificationSourceKind = 'portal-project-message';
export const communicationsPortalProjectMessageSystemNotificationTitle = 'Customer message received';
const lowercaseUuidPattern = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const portalProjectActionPathSchema = z.string().regex(new RegExp(`^/projects/${lowercaseUuidPattern}$`));
export const communicationsPortalProjectNotificationRequestedPayloadSchema = z.strictObject({
    messageId: z.string().guid(),
    projectId: z.string().guid(),
    audienceStrategy: z.literal(communicationsPortalProjectNotificationAudienceStrategy),
});
export const communicationsPortalProjectMessageSystemNotificationSourceSchema = z.strictObject({
    sourceModuleId: z.literal(communicationsPortalProjectMessageSystemNotificationSourceModule),
    sourceKind: z.literal(communicationsPortalProjectMessageSystemNotificationSourceKind),
    sourceFactId: z.string().guid(),
    sourceEventId: z.string().guid(),
    sourceEventName: z.literal(communicationsPortalProjectNotificationRequestedEventName),
    sourceEventVersion: z.literal(communicationsPortalProjectNotificationRequestedEventVersion),
    sourceOccurredAt: z.string().datetime({ offset: true }),
    recipientMembershipId: z.string().guid(),
    title: z.literal(communicationsPortalProjectMessageSystemNotificationTitle),
    body: z.literal('A customer posted a message to a Project.'),
    actionPath: portalProjectActionPathSchema,
});
export const communicationsPortalProjectMessageSystemNotificationRowSchema = z.strictObject({
    intent_id: z.string().guid(),
    source_event_id: z.string().guid(),
    source_occurred_at: z.string().datetime({ offset: true }),
    recipient_membership_id: z.string().guid(),
    message_id: z.string().guid(),
    project_id: z.string().guid(),
    action_path: portalProjectActionPathSchema,
});
export function composeCommunicationsPortalProjectMessageSystemNotificationBody(_input) {
    return 'A customer posted a message to a Project.';
}
//# sourceMappingURL=communications-portal-project-message.contract.js.map